var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/** Display order: what works, what wants doing, what is fine as it is. */
export var FIELD_FORM_STATUSES = [
    'inForm', 'missingFromForm', 'notFormMaterial', 'unknown',
];
/** Shares the browser's palette: green = goal state, amber = actionable, grey = by design. */
export var fieldFormStatusColor = function (status) {
    if (status === 'inForm')
        return '#128000';
    if (status === 'missingFromForm')
        return '#b06000';
    if (status === 'notFormMaterial')
        return '#5a6b7d';
    return '#8a8a8a';
};
var isPlanned = function (rule) { return rule.status === 'planned'; };
/** A `recordTypePattern` (`VOCABULARY_*`) names no single type and has no template. */
var literalRecordType = function (rule) { return (rule.recordTypePattern ? undefined : rule.recordType); };
/**
 * Authority types that derive to `className`.
 *
 * <p>A record-group class (`AdminMetadata`) is carried by EVERY authority record, so
 * it derives to every type that ships a form — which is why `availableRecordTypes`
 * exists and why this cannot be answered from `classDerivation`: `AUTHOR_WORK` ships
 * `author-work.json` under a `planned` rule, so that axis knows 10 of 11.
 *
 * @param availableRecordTypes types the tenant actually has a template for. Omitted
 *   (or empty) falls back to the types `classDerivation` names, which is right for an
 *   entity class and under-reports a record-group one — so callers that can supply it
 *   must.
 */
export var recordTypesForClass = function (catalog, className, availableRecordTypes) {
    var _a, _b, _c;
    if (availableRecordTypes === void 0) { availableRecordTypes = []; }
    var rules = (_a = catalog === null || catalog === void 0 ? void 0 : catalog.classDerivation) !== null && _a !== void 0 ? _a : [];
    var group = (_c = (_b = catalog === null || catalog === void 0 ? void 0 : catalog.classes) === null || _b === void 0 ? void 0 : _b[className]) === null || _c === void 0 ? void 0 : _c.group;
    if (group === 'record') {
        if (availableRecordTypes.length > 0) {
            return { types: Array.from(new Set(availableRecordTypes)).sort(), planned: [] };
        }
        var all_1 = new Set();
        rules.forEach(function (rule) {
            var type = literalRecordType(rule);
            if (type && !isPlanned(rule))
                all_1.add(type);
        });
        return { types: Array.from(all_1).sort(), planned: [] };
    }
    var types = new Set();
    var planned = new Set();
    rules.forEach(function (rule) {
        if (rule.class !== className)
            return;
        var type = literalRecordType(rule);
        if (!type)
            return;
        (isPlanned(rule) ? planned : types).add(type);
    });
    return { types: Array.from(types).sort(), planned: Array.from(planned).sort() };
};
/**
 * Whether `classDerivation` says anything at all about this type. False for a type
 * that ships a form ahead of its rule — see the module header.
 */
export var hasClassDerivation = function (catalog, recordType) {
    var _a;
    return ((_a = catalog === null || catalog === void 0 ? void 0 : catalog.classDerivation) !== null && _a !== void 0 ? _a : []).some(function (rule) { return literalRecordType(rule) === recordType; });
};
/** Classes an authority type derives to, plus the record-level classes it always carries. */
export var classesForRecordType = function (catalog, recordType) {
    var _a, _b;
    var out = new Set();
    ((_a = catalog === null || catalog === void 0 ? void 0 : catalog.classDerivation) !== null && _a !== void 0 ? _a : []).forEach(function (rule) {
        // `planned` is deliberately NOT skipped here. It qualifies the TYPE, not the
        // class assignment: `AUTHOR_WORK` is not live yet, but the rule states plainly
        // that it is a `Work`. Skipping it left AUTHOR_WORK carrying only the
        // record-level classes, so its shipped form read as 11 unbound tags — a flood of
        // false reverse findings, in place of the four false amber ones this fixed.
        if (literalRecordType(rule) === recordType)
            out.add(rule.class);
    });
    Object.entries((_b = catalog === null || catalog === void 0 ? void 0 : catalog.classes) !== null && _b !== void 0 ? _b : {}).forEach(function (_a) {
        var name = _a[0], def = _a[1];
        if (def.group === 'record')
            out.add(name);
    });
    return Array.from(out).sort();
};
/**
 * Tag to the subfield codes the ontology binds for any of `classNames`.
 *
 * <p>A tag with a bind but no subfield (control fields) maps to an EMPTY set, which is
 * why callers must test key presence rather than set size.
 */
export var boundSubfieldsForClasses = function (catalog, classNames) {
    var _a;
    var wanted = new Set(classNames);
    var out = new Map();
    Object.entries((_a = catalog === null || catalog === void 0 ? void 0 : catalog.marc21Fields) !== null && _a !== void 0 ? _a : {}).forEach(function (_a) {
        var _b;
        var tag = _a[0], entry = _a[1];
        ((_b = entry.binds) !== null && _b !== void 0 ? _b : []).forEach(function (bind) {
            var _a, _b, _c;
            if (!wanted.has(bind.cls))
                return;
            var codes = (_a = out.get(tag)) !== null && _a !== void 0 ? _a : new Set();
            ((_b = bind.subfields) !== null && _b !== void 0 ? _b : []).forEach(function (code) { return codes.add(code); });
            if (bind.subfield)
                codes.add(bind.subfield);
            // Qualifiers refine a value rather than being a fact of their own (ADR-0007
            // D12), but they ARE subfields the form legitimately offers — omitting them
            // reported $7/$i/$w on every relationship field as unknown to the ontology.
            ((_c = bind.qualifiers) !== null && _c !== void 0 ? _c : []).forEach(function (q) { if (q.subfield)
                codes.add(q.subfield); });
            out.set(tag, codes);
        });
    });
    return out;
};
/** Tag to the subfield codes a compiled template offers, `formOnly` slots excluded. */
export var subfieldsInForm = function (config) {
    var _a;
    var out = new Map();
    ((_a = config === null || config === void 0 ? void 0 : config.tabs) !== null && _a !== void 0 ? _a : []).forEach(function (tab) {
        var _a;
        ((_a = tab.fields) !== null && _a !== void 0 ? _a : []).forEach(function (field) {
            var _a, _b;
            if (!(field === null || field === void 0 ? void 0 : field.tag))
                return;
            var codes = (_a = out.get(field.tag)) !== null && _a !== void 0 ? _a : new Set();
            ((_b = field.subfields) !== null && _b !== void 0 ? _b : []).forEach(function (sub) {
                if (!(sub === null || sub === void 0 ? void 0 : sub.code) || sub.formOnly)
                    return;
                codes.add(sub.code);
            });
            out.set(field.tag, codes);
        });
    });
    return out;
};
/**
 * A form could plausibly carry this field. See the module header — the two negatives
 * are `input_policy` and the absence of any subfield structure.
 */
export var isFormMaterial = function (entry) {
    var _a;
    if (!entry)
        return false;
    if (entry.input_policy && entry.input_policy !== 'allowed')
        return false;
    return ((_a = entry.subfields) !== null && _a !== void 0 ? _a : []).length > 0;
};
var czech = new Intl.Collator('cs', { sensitivity: 'base', numeric: true });
export var buildOntologyFormMap = function (_a) {
    var _b, _c, _d, _e, _f;
    var catalog = _a.catalog, className = _a.className, forms = _a.forms, _g = _a.availableRecordTypes, availableRecordTypes = _g === void 0 ? [] : _g;
    var classDef = (_b = catalog === null || catalog === void 0 ? void 0 : catalog.classes) === null || _b === void 0 ? void 0 : _b[className];
    var _h = recordTypesForClass(catalog, className, availableRecordTypes), types = _h.types, planned = _h.planned;
    /**
     * Forward comparison counts a planned type's template too: the type is not live but
     * the FORM ships, and a tag it offers is offered. Excluding it painted four of
     * Work's fields amber against a form that has them.
     */
    var comparable = new Set(__spreadArray(__spreadArray([], types, true), planned, true));
    var usable = forms.filter(function (f) { return !!f.config
        && comparable.has(f.recordType); });
    var formsResolved = usable.length > 0;
    // ---- nodes and edges: the ROOTED class only ---------------------------
    var boundForClass = boundSubfieldsForClasses(catalog, [className]);
    var propertiesOnTag = new Map();
    var tagsOfProperty = new Map();
    var edges = [];
    var seenEdge = new Set();
    Object.entries((_c = catalog === null || catalog === void 0 ? void 0 : catalog.marc21Fields) !== null && _c !== void 0 ? _c : {}).forEach(function (_a) {
        var _b;
        var tag = _a[0], entry = _a[1];
        ((_b = entry.binds) !== null && _b !== void 0 ? _b : []).forEach(function (bind) {
            var _a, _b;
            if (bind.cls !== className)
                return;
            var key = "".concat(bind.property, " ").concat(tag);
            if (seenEdge.has(key))
                return;
            seenEdge.add(key);
            edges.push({ property: bind.property, tag: tag });
            propertiesOnTag.set(tag, __spreadArray(__spreadArray([], ((_a = propertiesOnTag.get(tag)) !== null && _a !== void 0 ? _a : []), true), [bind.property], false));
            tagsOfProperty.set(bind.property, __spreadArray(__spreadArray([], ((_b = tagsOfProperty.get(bind.property)) !== null && _b !== void 0 ? _b : []), true), [tag], false));
        });
    });
    var formIndex = usable.map(function (f) { return ({
        recordType: f.recordType,
        tags: subfieldsInForm(f.config),
    }); });
    var statusOf = function (tag) {
        var _a;
        if (!formsResolved)
            return { status: 'unknown', offeredBy: [] };
        var offeredBy = Array.from(new Set(formIndex.filter(function (f) { return f.tags.has(tag); }).map(function (f) { return f.recordType; }))).sort();
        if (offeredBy.length > 0)
            return { status: 'inForm', offeredBy: offeredBy };
        return {
            status: isFormMaterial((_a = catalog === null || catalog === void 0 ? void 0 : catalog.marc21Fields) === null || _a === void 0 ? void 0 : _a[tag]) ? 'missingFromForm' : 'notFormMaterial',
            offeredBy: [],
        };
    };
    var fields = Array.from(boundForClass.keys())
        .sort(function (a, b) { return czech.compare(a, b); })
        .map(function (tag) {
        var _a, _b, _c, _d, _e;
        var _f = statusOf(tag), status = _f.status, offeredBy = _f.offeredBy;
        return {
            tag: tag,
            label: (_d = (_c = (_b = (_a = catalog === null || catalog === void 0 ? void 0 : catalog.marc21Fields) === null || _a === void 0 ? void 0 : _a[tag]) === null || _b === void 0 ? void 0 : _b.label) === null || _c === void 0 ? void 0 : _c.cs) !== null && _d !== void 0 ? _d : tag,
            status: status,
            properties: Array.from(new Set((_e = propertiesOnTag.get(tag)) !== null && _e !== void 0 ? _e : [])).sort(),
            offeredBy: offeredBy,
        };
    });
    /**
     * Properties come from `domain` — what the class MAY carry — not from the binds,
     * so a property declared on the class with no MARC projection still appears. That
     * is a finding of its own and a binds-only list would hide it.
     */
    var properties = Object.entries((_d = catalog === null || catalog === void 0 ? void 0 : catalog.properties) !== null && _d !== void 0 ? _d : {})
        .filter(function (_a) {
        var _b;
        var def = _a[1];
        return ((_b = def.domain) !== null && _b !== void 0 ? _b : []).includes(className);
    })
        .map(function (_a) {
        var _b, _c, _d;
        var name = _a[0], def = _a[1];
        return ({
            name: name,
            label: (_c = (_b = def.label) === null || _b === void 0 ? void 0 : _b.cs) !== null && _c !== void 0 ? _c : name,
            valueType: def.valueType,
            cardinality: def.cardinality,
            tags: Array.from(new Set((_d = tagsOfProperty.get(name)) !== null && _d !== void 0 ? _d : []))
                .sort(function (a, b) { return czech.compare(a, b); }),
        });
    })
        .sort(function (a, b) { return czech.compare(a.label, b.label); });
    // ---- reverse gap: judged against the WIDER set ------------------------
    var reverseFields = [];
    var unknownSubfields = [];
    var unboundSubfields = [];
    /**
     * Reverse needs the type's classes; a type with no derivation rule has none, and
     * judging it anyway reports nearly its whole form as unbound. See the module header.
     */
    var judgeable = usable.filter(function (f) { return hasClassDerivation(catalog, f.recordType); });
    var typesNotJudgedInReverse = Array.from(new Set(usable.filter(function (f) { return !hasClassDerivation(catalog, f.recordType); }).map(function (f) { return f.recordType; }))).sort();
    if (formsResolved) {
        var fieldGap_1 = new Map();
        var gaps_1 = {
            unknown: new Map(), unbound: new Map(),
        };
        judgeable.forEach(function (form) {
            var boundForType = boundSubfieldsForClasses(catalog, classesForRecordType(catalog, form.recordType));
            subfieldsInForm(form.config).forEach(function (codes, tag) {
                var _a, _b, _c, _d, _e;
                var bound = boundForType.get(tag);
                if (!bound) {
                    fieldGap_1.set(tag, ((_a = fieldGap_1.get(tag)) !== null && _a !== void 0 ? _a : new Set()).add(form.recordType));
                    return;
                }
                // A tag bound with NO subfield (a control field) maps to an empty set. Forward
                // it is excused as `notFormMaterial`; reverse, every code the form offers would
                // read as unbound — the same tag, two opposite verdicts on one screen. Unreachable
                // on today's ontology, so this is a guard rather than a fix.
                if (bound.size === 0 && !isFormMaterial((_b = catalog === null || catalog === void 0 ? void 0 : catalog.marc21Fields) === null || _b === void 0 ? void 0 : _b[tag]))
                    return;
                var extra = Array.from(codes).filter(function (code) { return !bound.has(code); });
                if (extra.length === 0)
                    return;
                // Split on the STRUCTURAL layer, not the semantic one — see ReverseSubfieldGap.
                var structural = new Set(((_e = (_d = (_c = catalog === null || catalog === void 0 ? void 0 : catalog.marc21Fields) === null || _c === void 0 ? void 0 : _c[tag]) === null || _d === void 0 ? void 0 : _d.subfields) !== null && _e !== void 0 ? _e : []).map(function (sub) { return sub.code; }));
                extra.forEach(function (code) {
                    var _a;
                    var kind = structural.has(code) ? 'unbound' : 'unknown';
                    var entry = (_a = gaps_1[kind].get(tag)) !== null && _a !== void 0 ? _a : { codes: new Set(), types: new Set() };
                    entry.codes.add(code);
                    entry.types.add(form.recordType);
                    gaps_1[kind].set(tag, entry);
                });
            });
        });
        fieldGap_1.forEach(function (recordTypes, tag) {
            var _a, _b;
            var entry = (_a = catalog === null || catalog === void 0 ? void 0 : catalog.marc21Fields) === null || _a === void 0 ? void 0 : _a[tag];
            reverseFields.push({
                tag: tag,
                label: (_b = entry === null || entry === void 0 ? void 0 : entry.label) === null || _b === void 0 ? void 0 : _b.cs,
                knownToOntology: !!entry,
                recordTypes: Array.from(recordTypes).sort(),
            });
        });
        ['unknown', 'unbound'].forEach(function (kind) {
            var into = kind === 'unknown' ? unknownSubfields : unboundSubfields;
            gaps_1[kind].forEach(function (_a, tag) {
                var codes = _a.codes, gapTypes = _a.types;
                into.push({
                    tag: tag,
                    kind: kind,
                    codes: Array.from(codes).sort(), recordTypes: Array.from(gapTypes).sort(),
                });
            });
            into.sort(function (a, b) { return czech.compare(a.tag, b.tag); });
        });
        reverseFields.sort(function (a, b) { return czech.compare(a.tag, b.tag); });
    }
    var counts = FIELD_FORM_STATUSES.reduce(function (acc, status) {
        acc[status] = fields.filter(function (f) { return f.status === status; }).length;
        return acc;
    }, {});
    return {
        className: className,
        classLabel: (_f = (_e = classDef === null || classDef === void 0 ? void 0 : classDef.label) === null || _e === void 0 ? void 0 : _e.cs) !== null && _f !== void 0 ? _f : className,
        group: classDef === null || classDef === void 0 ? void 0 : classDef.group,
        recordTypes: Array.from(new Set(usable.map(function (f) { return f.recordType; }))).sort(),
        plannedRecordTypes: planned,
        typesNotJudgedInReverse: typesNotJudgedInReverse,
        templates: usable.map(function (f) { return "".concat(f.recordType, "/").concat(f.templateId); }).sort(),
        properties: properties,
        fields: fields,
        edges: edges,
        reverseFields: reverseFields,
        unknownSubfields: unknownSubfields,
        unboundSubfields: unboundSubfields,
        counts: counts,
        formsResolved: formsResolved,
    };
};
